/* global chrome */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getQueryResults, updateResultStatus, postQuery, crawlQueryResult } from '../lib/QueryDataService';
import { getProfile } from '../lib/ProfileDataService';
import { Search as Magnifier, Plus, Globe, BarChart3, ListOrdered, Loader2, Scan } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import QueryResultsTable from '../components/QueryResultsTable';
import ProfileSelectionModal from '../components/ui/ProfileSelectionModal';
import AiContentModal from '../components/ui/AiContentModal';
import ContactsModal from '../components/ui/ContactsModal';
// import RefinementModal from '../components/ui/RefinementModal';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ExtensionNoticeModal } from '../components/ui/ExtensionSubmit';

const LAST_SELECTED_PROFILE_KEY = 'lastSelectedProfile';

const QueryResults = () => {
  const { queryId } = useParams();
  const [searchParams] = useSearchParams();
  const searchTerm = searchParams.get('searchTerm');
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [crawlingResultIds, setCrawlingResultIds] = useState([]);
  const [bulkScanning, setBulkScanning] = useState(false);
  const bulkScanOffsetRef = useRef(0);
  
  // Auto-scan state - DISABLED
  // const [autoScanning, setAutoScanning] = useState(false);
  // const [scanQueue, setScanQueue] = useState([]);
  // const [scannedCount, setScannedCount] = useState(0);
  // const [scanErrors, setScanErrors] = useState(0);
  // const autoScanCompleted = useRef(false);
  const [scanQueue] = useState([]);
  
  const [selectedStatus, setSelectedStatus] = useState(1); // Default to Active
  const [filterText, setFilterText] = useState(''); // Client-side filter for title/url
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [aiContentModalOpen, setAiContentModalOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [userClosedAiModal, setUserClosedAiModal] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showProgressPopup, setShowProgressPopup] = useState(false);
  const [progressStep, setProgressStep] = useState(1);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(null);
  // const [showRefinementModal, setShowRefinementModal] = useState(false);
  const { t } = useTranslation();

  // Filter results by all fields (client-side)
  const filteredResults = useMemo(() => {
    if (!filterText.trim()) return results;
    const searchLower = filterText.toLowerCase();
    return results.filter(result => 
      Object.values(result).some(value => 
        value && String(value).toLowerCase().includes(searchLower)
      )
    );
  }, [results, filterText]);

  // Check if there are any results that haven't been crawled yet (scan buttons available)
  const hasScannableResults = useMemo(() => {
    return filteredResults.some(result => result.crawled !== true);
  }, [filteredResults]);

  const fetchResults = useCallback(async (status = selectedStatus) => {
      try {
      setError(null);
      const data = await getQueryResults(queryId, status);
      // API returns { queryId, results, totalCount }
      setResults(data.results || []);
      } catch (err) {
        setError('Failed to fetch results');
      console.error('Error fetching results:', err);
      }
    }, [queryId, selectedStatus]);

  // Silent refresh - updates data without showing loading state
  const refreshResults = useCallback(async (status = selectedStatus) => {
    try {
      const data = await getQueryResults(queryId, status, false); // showLoader = false
      setResults(data.results || []);
    } catch (err) {
      console.error('Error refreshing results:', err);
    }
  }, [queryId, selectedStatus]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Auto-scan function - scans first 20 eligible results in batches - DISABLED
  // const autoScanResults = useCallback(async () => {
  //   if (autoScanCompleted.current || results.length === 0) {
  //     return;
  //   }

  //   // Filter eligible results for scanning
  //   const eligibleResults = results.filter(result => 
  //     result.crawled !== true && 
  //     (!result.url || !result.url.toLowerCase().includes('openstreetmap'))
  //   );

  //   // Take first 20
  //   const resultsToScan = eligibleResults.slice(0, 20);
  //   
  //   if (resultsToScan.length === 0) {
  //     return;
  //   }

  //   autoScanCompleted.current = true;
  //   setAutoScanning(true);
  //   setScanQueue(resultsToScan.map(r => r.id));
  //   setScannedCount(0);
  //   setScanErrors(0);

  //   const BATCH_SIZE = 5;

  //   // Process in batches
  //   for (let i = 0; i < resultsToScan.length; i += BATCH_SIZE) {
  //     const batch = resultsToScan.slice(i, i + BATCH_SIZE);
  //     
  //     // Scan batch in parallel
  //     const batchResults = await Promise.allSettled(
  //       batch.map(result => crawlQueryResult(result.id))
  //     );

  //     // Update counts based on batch results
  //     batchResults.forEach((result, index) => {
  //       if (result.status === 'rejected') {
  //         console.error(`Error scanning ${batch[index].url}:`, result.reason);
  //         setScanErrors(prev => prev + 1);
  //       }
  //       setScannedCount(prev => prev + 1);
  //     });
  //   }

  //   // Single refresh after all scans complete - silent refresh without loading state
  //   try {
  //     await refreshResults();
  //   } catch (err) {
  //     console.error('Error refreshing results:', err);
  //   } finally {
  //     setAutoScanning(false);
  //     setScanQueue([]);
  //   }
  // }, [results, refreshResults]);

  // Trigger auto-scan on initial load - DISABLED
  // useEffect(() => {
  //   if (!loading && results.length > 0 && !autoScanCompleted.current) {
  //     autoScanResults();
  //   }
  // }, [loading, results, autoScanResults]);

  // Check if any results have Submit buttons (isValid === true) or contacts links
  // useEffect(() => {
  //   if (!loading && results.length > 0) {
  //     const hasSubmitButtons = results.some(result => result.isValid === true);
  //     const hasContactsLinks = results.some(result => 
  //       result.contacts && 
  //       result.contacts !== '' && 
  //       result.contacts.trim() !== ''
  //     );
  //     
  //     // Only show refinement modal if there are no submit buttons AND no contacts links
  //     if (!hasSubmitButtons && !hasContactsLinks) {
  //       setShowRefinementModal(true);
  //     } else {
  //       setShowRefinementModal(false);
  //     }
  //   }
  // }, [results, loading]);

  // This useEffect is now simplified since profile checking is handled in handleRowAction
  // Keep it for any edge cases where selectedResult changes without user action
  useEffect(() => {
    const checkSavedProfile = async () => {
      if (!selectedResult || selectedProfile || profileModalOpen || aiContentModalOpen || userClosedAiModal) {
        return; // Skip if no result selected, profile already selected, modals already open, or user closed modal
      }
      
      // This is now mainly a fallback - the main logic is in handleRowAction
    };

    checkSavedProfile();
  }, [selectedResult, selectedProfile, profileModalOpen, aiContentModalOpen, userClosedAiModal]);

  // Handle status filter change
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  // Check if Chrome extension is available
  const checkChromeExtension = () => {
    if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
      return false;
    }
    return true;
  };

  // Get current progress step details
  const getProgressStepDetails = (step) => {
    switch (step) {
      case 1:
        return {
          icon: <Globe className="h-8 w-8 text-blue-600 animate-pulse" />,
          text: t('researchingSites')
        };
      case 2:
        return {
          icon: <BarChart3 className="h-8 w-8 text-green-600 animate-pulse" />,
          text: t('analyzingRelevance')
        };
      case 3:
        return {
          icon: <ListOrdered className="h-8 w-8 text-orange-600 animate-pulse" />,
          text: t('prioritizing')
        };
      case 4:
        return {
          icon: <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />,
          text: t('almostThere')
        };
      default:
        return {
          icon: <Globe className="h-8 w-8 text-blue-600 animate-pulse" />,
          text: t('researchingSites')
        };
    }
  };

  // Handle "More Results" button click
  const handleMoreResults = async () => {
    if (!searchTerm || loadingMore) return;
    
    // Check if Chrome extension is available first
    if (!checkChromeExtension()) {
      setShowExtensionModal(true);
      return;
    }
    
    try {
      setLoadingMore(true);
      setShowProgressPopup(true);
      setProgressStep(1);
      
      let apiCompleted = false;
      
      // Start API call and progress animation in parallel
      const apiPromise = (async () => {
        // Calculate page number right before API call to ensure we have the latest results count
        const nextPage = Math.floor(results.length / 10) + 1;
        
        // Create a new search query with the same search term, next page, and current QueryId
        const response = await postQuery({ 
          searchTerm, 
          searchType: 1, // Assuming searchType 1 for now, you might want to get this from context
          page: nextPage,
          queryId: queryId, // Pass the current QueryId from the route
          showLoader: false // Disable global loader during multi-step progress
        });
        
        // Wait for the search to complete and then fetch the results
        const newResults = await getQueryResults(response.queryId, selectedStatus, false);
        
        // Replace results with fresh data from API
        if (newResults.results && newResults.results.length > 0) {
          setResults(newResults.results);
        }
        
        apiCompleted = true;
      })();

      (async () => {
        // Step 1: Researching relevant sites (4 seconds)
        await new Promise(resolve => setTimeout(resolve, 4000));
        if (apiCompleted) return;
        
        // Step 2: Analyzing relevance (4 seconds)
        setProgressStep(2);
        await new Promise(resolve => setTimeout(resolve, 4000));
        if (apiCompleted) return;
        
        // Step 3: Prioritizing (4 seconds)
        setProgressStep(3);
        await new Promise(resolve => setTimeout(resolve, 4000));
        if (apiCompleted) return;
        
        // Step 4: Compiling (4 seconds)
        setProgressStep(4);
        await new Promise(resolve => setTimeout(resolve, 4000));
      })();

      // Wait for API call to complete (progress animation will stop early if API is done)
      await apiPromise;
      
    } catch (error) {
      console.error('Error loading more results:', error);
      setError('Failed to load more results');
    } finally {
      setShowProgressPopup(false);
      setLoadingMore(false);
    }
  };

  const handleRowAction = async (action, row) => {
    if (action === 'submit') {
      setSelectedResult(row);
      setUserClosedAiModal(false); // Reset the flag when selecting a new result
      
      // Check if there's a saved profile and skip modal if found
      try {
        const savedProfileId = localStorage.getItem(LAST_SELECTED_PROFILE_KEY);
        if (savedProfileId) {
          const profile = await getProfile(savedProfileId);
          if (profile) {
            // Skip profile modal and go directly to AI content modal
            setSelectedProfile(profile);
            setAiContentModalOpen(true);
            console.log('Auto-loaded saved profile and skipped modal:', profile.name);
            return;
          }
        }
      } catch (error) {
        console.log('No saved profile found or error loading:', error.message);
      }
      
      // No saved profile found, open profile selection modal
      setProfileModalOpen(true);
      return;
    }

    if (action === 'contacts') {
      // Parse the contacts JSON string if it's a string, otherwise use as is
      let contactsData;
      try {
        if (typeof row.contacts === 'string') {
          console.log('Raw contacts string:', row.contacts);
          contactsData = JSON.parse(row.contacts);
        } else {
          contactsData = row.contacts;
        }
        console.log('Parsed contacts data:', contactsData);
      } catch (error) {
        console.error('Error parsing contacts data:', error);
        console.log('Failed to parse contacts string:', row.contacts);
        
        // Try to handle truncated JSON by attempting to fix it
        if (typeof row.contacts === 'string' && row.contacts.includes('"emails"') && row.contacts.includes('"phones"')) {
          try {
            // Try to fix truncated JSON by adding missing closing braces
            let fixedJson = row.contacts.trim();
            if (!fixedJson.endsWith('}')) {
              // Count opening and closing braces to determine how many are missing
              const openBraces = (fixedJson.match(/\{/g) || []).length;
              const closeBraces = (fixedJson.match(/\}/g) || []).length;
              const missingBraces = openBraces - closeBraces;
              fixedJson += ']'.repeat(missingBraces > 0 ? missingBraces : 0) + '}';
            }
            console.log('Attempting to fix truncated JSON:', fixedJson);
            contactsData = JSON.parse(fixedJson);
          } catch (fixError) {
            console.error('Failed to fix truncated JSON:', fixError);
            contactsData = null;
          }
        } else {
          contactsData = null;
        }
      }
      
      setSelectedContacts(contactsData);
      setShowContactsModal(true);
      return;
    }

    if (action === 'checkWebsite') {
      // Add this ID to the array immediately
      setCrawlingResultIds(prev => [...prev, row.id]);
      
      // Fire and forget - don't await so multiple scans can run in parallel
      crawlQueryResult(row.id)
        .then(() => {
          console.log('Website check completed for:', row.url);
          refreshResults(); // Silent refresh
        })
        .catch((err) => {
          console.error('Error checking website:', err);
          setError('Failed to check website');
        })
        .finally(() => {
          // Remove only this specific ID from the array
          setCrawlingResultIds(prev => prev.filter(id => id !== row.id));
        });
      
      return;
    }

    try {
      setUpdating(true);
      
      switch (action) {
        case 'delete':
          // If viewing deleted items (status=2), undelete to active (status=1)
          // If viewing active/archived items, delete them (status=2)
          const deleteStatus = selectedStatus === 2 ? 1 : 2;
          await updateResultStatus(row.id, deleteStatus);
          await fetchResults(); // Refresh the list
          break;
        case 'archive':
          // If viewing archived items (status=0), unarchive to active (status=1)
          // If viewing active items (status=1), archive to archived (status=0)
          const newStatus = selectedStatus === 0 ? 1 : 0;
          await updateResultStatus(row.id, newStatus);
          await fetchResults(); // Refresh the list
          break;
        default:
          console.log('Unknown action:', action, row);
      }
    } catch (err) {
      console.error('Error updating result status:', err);
      setError('Failed to update result status');
    } finally {
      setUpdating(false);
    }
  };

  const handleProfileNext = (profile) => {
    // Store selected profile and open AI content modal
    setSelectedProfile(profile);
    setProfileModalOpen(false); // Close profile modal
    setAiContentModalOpen(true); // Open AI content modal
    setUserClosedAiModal(false); // Reset flag since user went through normal flow
  };

  const handleAiContentModalClose = () => {
    setAiContentModalOpen(false);
    setSelectedProfile(null);
    setUserClosedAiModal(true); // Mark that user explicitly closed the modal
  };

  const handleChangeProfile = () => {
    // Close AI content modal and open profile selection modal
    setAiContentModalOpen(false);
    setProfileModalOpen(true);
    setUserClosedAiModal(false); // Reset flag when changing profiles
  };

  // Bulk scan - clicks Scan buttons one by one with 1 second delay
  const handleBulkScan = () => {
    if (bulkScanning) return; // Already running
    
    // Find all Scan buttons in the DOM
    const scanButtons = document.querySelectorAll('[data-scan-button="true"]');
    const buttonsArray = Array.from(scanButtons);
    
    if (buttonsArray.length === 0) {
      console.log('No scan buttons found');
      return;
    }
    
    // Get the next 10 buttons starting from the offset
    const startIndex = bulkScanOffsetRef.current;
    const buttonsToClick = buttonsArray.slice(startIndex, startIndex + 10);
    
    if (buttonsToClick.length === 0) {
      // Reset offset if we've gone through all buttons
      bulkScanOffsetRef.current = 0;
      console.log('Reset bulk scan offset to 0');
      return;
    }
    
    setBulkScanning(true);
    let clickIndex = 0;
    
    const intervalId = setInterval(() => {
      if (clickIndex >= buttonsToClick.length) {
        // Done clicking all buttons in this batch
        clearInterval(intervalId);
        setBulkScanning(false);
        // Update offset for next batch
        bulkScanOffsetRef.current = startIndex + buttonsToClick.length;
        console.log('Bulk scan complete, next offset:', bulkScanOffsetRef.current);
        return;
      }
      
      const button = buttonsToClick[clickIndex];
      if (button && !button.disabled) {
        button.click();
        console.log('Clicked scan button', clickIndex + 1, 'of', buttonsToClick.length);
      }
      clickIndex++;
    }, 1000);
  };

  // Reset bulk scan offset when results change
  useEffect(() => {
    bulkScanOffsetRef.current = 0;
  }, [results]);

  // Status filter navigation (Active, Archived, Deleted)
  const statusTabs = [
    { value: 1, label: t('active') },
    { value: 0, label: t('archived') },
    { value: 2, label: t('deleted') },
  ];

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2 sm:gap-3">
          <Magnifier className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
          {searchTerm ? `Search Results for "${searchTerm}"` : t('searchResults')}
        </h1>
        {filteredResults.length > 0 && (
          <p className="text-sm text-muted-foreground mt-1">
            {filteredResults.length} {filteredResults.length === 1 ? t('result') : t('results')}
          </p>
        )}
      </div>

      {/* Auto-Scan Progress Banner - DISABLED */}
      {/* {autoScanning && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-900">
                      {t('autoScanning')} ({scannedCount}/{scanQueue.length})
                    </span>
                    {scanErrors > 0 && (
                      <span className="text-xs text-orange-600">
                        {scanErrors} {t('errors')}
                      </span>
                    )}
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(scannedCount / scanQueue.length) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setAutoScanning(false);
                  setScanQueue([]);
                }}
                className="ml-4"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )} */}

      {/* Status Filter Navigation */}
      <div className="flex gap-4 mb-4">
        {statusTabs.map(tab => {
          const isActive = selectedStatus === tab.value;
          const isPrimaryTab = tab.value === 1; // Active tab is primary
          
          let buttonClasses = 'px-3 py-1 rounded-full border text-sm transition-colors ';
          
          if (isActive) {
            if (isPrimaryTab) {
              // Active primary tab - most prominent
              buttonClasses += 'bg-primary text-primary-foreground border-primary font-semibold shadow-md';
            } else {
              // Active secondary tab - standard active styling
              buttonClasses += 'bg-primary text-primary-foreground border-primary font-medium';
            }
          } else {
            if (isPrimaryTab) {
              // Inactive primary tab - more prominent than other inactive tabs
              buttonClasses += 'bg-primary/10 text-primary border-primary/30 font-medium hover:bg-primary/20';
            } else {
              // Inactive secondary tabs - subtle
              buttonClasses += 'bg-muted text-muted-foreground border-muted-foreground/20 font-normal hover:bg-primary/10';
            }
          }
          
          return (
            <button
              key={tab.value}
              className={buttonClasses}
              onClick={() => handleStatusChange(tab.value)}
            >
              {tab.label}
            </button>
          );
        })}
        
        {/* Filter Search Box */}
        <div className="flex-1 max-w-xs">
          <div className="relative">
            <Magnifier className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('filterResults')}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </div>

      {/* Fixed Bulk Scan Button - Bottom Right (Expandable FAB) - Only show if there are scannable results */}
      {hasScannableResults && (
        <div className="fixed bottom-6 right-6 z-50 group">
          <Button
            onClick={handleBulkScan}
            disabled={bulkScanning}
            variant="outline"
            className="h-14 rounded-full px-4 shadow-lg bg-gradient-to-r from-white to-yellow-50 border-yellow-400 text-yellow-700 hover:from-yellow-50 hover:to-yellow-100 hover:shadow-xl hover:shadow-yellow-200 transition-all duration-300 hover:border-yellow-500 overflow-hidden"
          >
            <div className="flex items-center">
              {bulkScanning ? (
                <Loader2 className="h-6 w-6 animate-spin flex-shrink-0" />
              ) : (
                <Scan className="h-6 w-6 flex-shrink-0" />
              )}
              <span className="max-w-0 overflow-hidden whitespace-nowrap group-hover:max-w-32 group-hover:ml-2 transition-all duration-300 font-semibold">
                {bulkScanning ? t('scanning') : t('bulkScan')}
              </span>
            </div>
          </Button>
        </div>
      )}

      {/* Results Cards */}
      <QueryResultsTable
        data={filteredResults}
        error={error}
        onRowAction={handleRowAction}
        selectedStatus={selectedStatus}
        updating={updating}
        crawlingResultIds={crawlingResultIds}
        scanQueue={scanQueue}
      />

      {/* More Results Button - Show if there are results */}
      {results.length < 0 && (
        <div className="flex justify-center mt-6">
          <Button
            onClick={handleMoreResults}
            disabled={loadingMore}
            className="font-semibold shadow-md bg-gradient-to-r from-white to-blue-50 border-blue-500 text-blue-600 hover:from-blue-50 hover:to-blue-100 hover:shadow-lg hover:shadow-blue-200 hover:scale-105 transition-all duration-300 hover:border-blue-600"
          >
            {loadingMore ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                {t('loading')}...
              </>
            ) : (
              <>
                <Plus className="mr-2 h-5 w-5" />
                {t('moreResults')}
              </>
            )}
          </Button>
        </div>
      )}

      {/* Profile Selection Modal */}
      <ProfileSelectionModal
        isOpen={profileModalOpen}
        onClose={() => {
          setProfileModalOpen(false);
          setUserClosedAiModal(true); // Prevent auto-navigation to AI content modal
          setSelectedResult(null); // Clear selected result to fully reset the flow
        }}
        onNext={handleProfileNext}
        selectedResult={selectedResult}
        resultTitle={selectedResult?.title}
        resultUrl={selectedResult?.url}
        disabled={updating}
      />

      {/* AI Content Modal */}
      <AiContentModal
        isOpen={aiContentModalOpen}
        onClose={handleAiContentModalClose}
        onChangeProfile={handleChangeProfile}
        selectedProfile={selectedProfile}
        selectedResult={selectedResult}
        resultTitle={selectedResult?.title}
        resultUrl={selectedResult?.url}
        disabled={updating}
      />

      {/* Search Progress Popup */}
      {showProgressPopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" />
          
          {/* Progress Modal */}
          <Card className="relative w-[480px] mx-4 shadow-xl">
            <CardContent className="p-8">
              <div className="text-center space-y-6">
                {/* Step Icon and Text */}
                <div className="flex items-center justify-center gap-4">
                  {getProgressStepDetails(progressStep).icon}
                  <span className="text-xl font-medium">
                    {getProgressStepDetails(progressStep).text}
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500 ease-out ${
                      progressStep === 1 ? 'w-1/4' : 
                      progressStep === 2 ? 'w-2/4' : 
                      progressStep === 3 ? 'w-3/4' : 'w-full'
                    }`}
                  />
                </div>
                
                {/* Step Indicator */}
                <p className="text-base text-muted-foreground">
                  Step {progressStep} of 4
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Chrome Extension Required Modal */}
      <ExtensionNoticeModal
        isOpen={showExtensionModal}
        onClose={() => setShowExtensionModal(false)}
      />

      {/* Contacts Modal */}
      <ContactsModal
        isOpen={showContactsModal}
        onClose={() => setShowContactsModal(false)}
        contacts={selectedContacts}
      />

      {/* Refinement Modal - DISABLED */}
      {/* <RefinementModal
        isOpen={showRefinementModal}
        onClose={() => setShowRefinementModal(false)}
      /> */}
    </div>
  );
};

export default QueryResults; 